import React, { useState, useEffect } from 'react';
import { groupBy } from 'lodash';
import { format } from 'date-fns';
import PropTypes from 'prop-types';

import notificationBannerHelper from 'components/consumer/NotificationBanner/utils';
import ProductCardLabelUI from 'components/consumer/product-card/ProductCardLabel/ProductCardLabelUI';
import ProtectionPlanModal from 'components/consumer/user-protection-plans/components/UserProtectionPlanModal';
import OrderDetailsModal from '../OrderDetailsModal';
import DisplayOrder from './DisplayOrder';

const formatPrice = price => {
  if (!price) {
    return null;
  }
  const formattedPrice = Number(price)
    .toFixed(2)
    .replace(/\d(?=(\d{3})+\.)/g, '$&,');
  return `$${formattedPrice}`;
};

const OrderInfo = ({
  isImpersonatingUser,
  isLoggedIn,
  isUpdateSuccess,
  order,
  purchaseErrorMessage,
  purchaseProtectionPlan,
  setIsUpdateSuccess,
  setPurchaseErrorMessage,
  showSignInPageUponSelection,
}) => {
  const [isDone, setIsDone] = useState(false);
  const [isShipped, setIsShipped] = useState(false);
  const [hasAllEFG, setHasAllEFG] = useState(false);
  const [groups, setGroups] = useState([]);
  const [noFabrics, setNoFabrics] = useState(null);
  const [orderDate, setOrderDate] = useState(null);
  const [fullDetailsOrderId, setFullDetailsOrderId] = useState(null);
  const [selectedPPState, setSelectedPPState] = useState({});
  const [selectedCartItemId, setSelectedCartItemId] = useState(null);
  const [isOpenTrackTooltip, openTrackingTooltip] = useState(false);

  useEffect(() => {
    if (!order) return;

    try {
      if (order.dated || order.order_date) {
        const date = format(
          new Date(order.dated || order.order_date),
          'MMM dd, yyyy'
        );
        if (date) {
          setOrderDate(date);
        }
      }
    } catch (e) {
      // ignore error;
    }

    const groupsList = [];
    const noFabricsMap = {};
    if (order.order_items?.length) {
      const groupObj = groupBy(
        order.order_items,
        i =>
          `${i.shipping_group}-${i.estimated_delivery}-${i.tracking_url || ''}`
      );
      if (groupObj) {
        Object.keys(groupObj).forEach(id => {
          groupsList.push({
            id,
            group: groupObj[id],
          });
        });
      }
      setGroups(groupsList);
      setHasAllEFG(
        order.order_items.filter(o => o.efg_flag === '1').length ===
          order.order_items.length
      );

      if (order.no_fabric) {
        order.order_items.forEach(item => {
          if (item.no_fabric && item.upholstery) {
            noFabricsMap[item.upholstery.trim()] = true;
          }
        });
        const fabricsList = Object.keys(noFabricsMap);
        if (fabricsList.length) {
          const lastFabric = fabricsList.pop();
          let text =
            'currently experiencing COVID-related delays. We expect building to resume on your';
          if (fabricsList.length) {
            text = `The fabrics you have selected, ${[
              fabricsList.join(', '),
              lastFabric,
            ].join('& ')}, are ${text} pieces shortly.`;
          } else {
            text = `The fabric you have selected, ${lastFabric}, is ${text} piece shortly.`;
          }
          setNoFabrics(text);
        }
      }
    }

    const statuses = [
      'Delivery Issue',
      'Delivered',
      'Processing Replacement',
      'Replacement',
      'Processing Exchange',
      'Donation',
      'Repair Needed',
      'Return Received',
      'Returned',
      'Refunded',
      'Canceled',
    ];
    const status =
      order.order_status === 'New_Not_Charged' ? 'New' : order.order_status;
    if (statuses.includes(status)) {
      setIsDone(true);
    }
    if (status === 'Shipped') {
      setIsShipped(true);
    }
  }, [order]);

  useEffect(() => {
    if (isUpdateSuccess && selectedPPState.selectedPlan) {
      notificationBannerHelper.showMsg('UserOrdersProtectionPlan', {
        planName: selectedPPState.selectedPlan.plan_name,
        productName: selectedPPState.cartItem?.name,
      });

      setSelectedPPState({
        selectedPlan: null,
        orderId: null,
        cartItem: null,
      });
      setIsUpdateSuccess(false);
    }
  }, [
    isUpdateSuccess,
    selectedPPState.cartItem?.name,
    selectedPPState.selectedPlan,
    setIsUpdateSuccess,
  ]);

  if (!order || !order.order_items || !order.order_items.length) {
    return <div className="p-8 text-center">No products found</div>;
  }

  return (
    <div
      className="mb-10 rounded-lg [border-bottom:1px_solid_#e5e5e5] lg:border lg:border-solid lg:border-gray-light6 lg:rounded-lg"
      data-xc="user-order"
      data-order-number={`J-${order.order_id}`}
    >
      <div className="w-full flex justify-between bg-brand-dark [background-image:url(https://joybird2.imgix.net/user-uploads-staging/joybird-teal-new.jpg?w=400&cs=srgb)] bg-cover lg:rounded-tl-lg lg:rounded-tr-lg">
        <div className="text-base font-bold text-white px-8 py-4 [&_span:hover]:text-white max-[1023px]:flex max-[1023px]:flex-col">
          <span>
            Order #: J-
            {order.order_id} &nbsp;
          </span>
          {orderDate ? (
            <>
              <span className="max-[1023px]:hidden">| &nbsp;</span>
              <span>Placed on: {orderDate}</span>
            </>
          ) : null}
        </div>
        <div className="relative min-w-[8rem] flex items-center justify-center lg:mt-2">
          {order.shipment_status && (
            <ProductCardLabelUI
              label={
                order.order_status === 'Canceled'
                  ? 'Canceled'
                  : order.shipment_status
              }
              color={
                order.order_status === 'Delivered'
                  ? 'brand'
                  : order.order_status === 'Canceled'
                  ? 'red-dark'
                  : 'gray'
              }
              variant="order-info"
            />
          )}
        </div>
      </div>
      <div className="bg-gray-light7 lg:p-6">
        {groups.map(({ id, group }, index) => (
          <DisplayOrder
            group={group}
            id={id}
            index={index}
            isDone={isDone}
            isImpersonatingUser={isImpersonatingUser}
            isLoggedIn={isLoggedIn}
            isOpenTrackTooltip={isOpenTrackTooltip}
            isShipped={isShipped}
            noFabrics={noFabrics}
            openTrackingTooltip={openTrackingTooltip}
            order={order}
            selectedCartItemId={selectedCartItemId}
            setSelectedCartItemId={setSelectedCartItemId}
            setSelectedPPState={setSelectedPPState}
            showSignInPageUponSelection={showSignInPageUponSelection}
          />
        ))}

        {!hasAllEFG && ['New', 'Shipped'].includes(order.order_status) && (
          <div className="px-0 py-2 [border-top:1px_solid_#ccc] max-[1023px]:pl-8 max-[1023px]:pr-8" />
        )}

        <div className="mb-6 bg-white rounded-lg last:mb-0">
          <div className="p-6 relative">
            <span className="text-sm">Order total: </span>
            <span className="text-sm font-bold">
              {formatPrice(order.total_order_amount)} &nbsp;
            </span>
          </div>
        </div>
      </div>
      {!!fullDetailsOrderId && (
        <OrderDetailsModal
          isOpen
          orderId={fullDetailsOrderId}
          onClose={() => setFullDetailsOrderId(null)}
        />
      )}

      {!!selectedPPState.orderId && (
        <ProtectionPlanModal
          onClose={() => {
            setPurchaseErrorMessage(null);
            setSelectedPPState({
              selectedPlan: null,
              orderId: null,
              cartItem: null,
            });
          }}
          orderId={selectedPPState.orderId}
          cartItem={selectedPPState.cartItem}
          selectedPlan={selectedPPState.selectedPlan}
          updateOrder={purchaseProtectionPlan}
          purchaseErrorMessage={purchaseErrorMessage}
        />
      )}
    </div>
  );
};

OrderInfo.propTypes = {
  isImpersonatingUser: PropTypes.bool,
  isLoggedIn: PropTypes.bool,
  isUpdateSuccess: PropTypes.bool,
  order: PropTypes.shape({
    order_id: PropTypes.string,
    order_status: PropTypes.string,
    shipment_status: PropTypes.string,
    total_order_amount: PropTypes.number,
    address_zip_code: PropTypes.string,
    order_date: PropTypes.string,
    dated: PropTypes.string,
    no_fabric: PropTypes.bool,
    order_items: PropTypes.arrayOf(
      PropTypes.shape({
        no_fabric: PropTypes.bool,
        cart_id: PropTypes.string,
        product_id: PropTypes.string,
        upholstery: PropTypes.string,
      })
    ),
  }),
  purchaseErrorMessage: PropTypes.string,
  purchaseProtectionPlan: PropTypes.func,
  setIsUpdateSuccess: PropTypes.func,
  setPurchaseErrorMessage: PropTypes.func,
  showSignInPageUponSelection: PropTypes.bool,
};

export default OrderInfo;
